import NiceModal from "@ebay/nice-modal-react"
import cx from "classnames"
import { Loader2 } from "lucide-react"
import { useMemo, useRef } from "react"
import { formatDuration, formatTaskCost } from "../../persistence/taskStatsUtils"
import { SessionThreadModel } from "../../store/SessionThreadModel"
import type { TaskSessionDebugInfo } from "../../store/TaskModel"
import { SessionThreadView } from "../SessionThreadView"
import { Modal } from "../ui"
import { isTaskSessionActive } from "./taskSessionTree"
import { taskSessionSubtitle, taskSessionTitle } from "./TaskSessionCard"

interface TaskSessionConversationModalProps {
    taskId: string
    session: TaskSessionDebugInfo
    taskSessions?: readonly TaskSessionDebugInfo[]
}

export const TaskSessionConversationModal = NiceModal.create(({ taskId, session, taskSessions }: TaskSessionConversationModalProps): JSX.Element => {
    const scrollElementRef = useRef<HTMLDivElement>(null)
    const threadModel = useMemo(() => {
        if (!session.nestSessionId) return null
        return new SessionThreadModel({
            sessionId: session.nestSessionId,
            harnessId: session.harnessId,
            providerSessionId: session.providerSessionId,
            cwd: session.cwd,
            model: session.model,
        })
    }, [session.cwd, session.harnessId, session.model, session.nestSessionId, session.providerSessionId])
    const active = isTaskSessionActive(session.status)

    return (
        <Modal title={taskSessionTitle(session)} size="xl">
            <div className="flex min-w-0 flex-col gap-3">
                <div className="flex min-w-0 flex-col gap-2 border border-border bg-base-200 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                        <div className="truncate text-xs font-medium text-base-content">{taskSessionSubtitle(session)}</div>
                        {session.cwd && <div className="mt-0.5 truncate font-mono text-[10px] text-muted">{session.cwd}</div>}
                    </div>
                    <div className="flex shrink-0 items-center gap-2 text-[11px] text-muted">
                        <span className="inline-flex items-center gap-1.5 capitalize">
                            {active ? (
                                <Loader2 size={11} className="animate-spin text-primary" />
                            ) : (
                                <span className={cx("h-1.5 w-1.5", sessionStatusDotClass(session.status))} />
                            )}
                            {session.status ?? "unknown"}
                        </span>
                        {session.usage?.costAvailable && <span className="tabular-nums">{formatTaskCost(session.usage.costUsd)}</span>}
                        {session.usage && session.usage.durationMs > 0 && <span className="tabular-nums">{formatDuration(session.usage.durationMs)}</span>}
                    </div>
                </div>

                {session.error && <div className="border border-error/30 bg-error/10 px-3 py-2 text-xs text-error">{session.error}</div>}

                <div
                    ref={scrollElementRef}
                    className="h-[min(65vh,44rem)] min-h-80 overflow-x-hidden overflow-y-auto border border-border bg-base-100"
                    data-session-transcript={session.nestSessionId ?? ""}
                >
                    {threadModel ? (
                        <SessionThreadView
                            model={threadModel}
                            taskId={taskId}
                            taskSessions={taskSessions}
                            scrollElementRef={scrollElementRef}
                            initialScrollToBottom
                        />
                    ) : (
                        <div className="px-4 py-5 text-sm text-muted">This historical reference has no readable Nest session.</div>
                    )}
                </div>
            </div>
        </Modal>
    )
})

function sessionStatusDotClass(status: string | undefined): string {
    if (status === "failed" || status === "error") return "bg-error"
    if (status === "cancelled" || status === "stopped") return "bg-warning"
    if (status === "idle" || status === "completed") return "bg-success"
    return "bg-muted"
}
